import { ITreasurerReport } from './../../models/reports/treasurer';
import { UserState } from './../../users/user.state';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IMember } from '../../models/member';
import { IOrganization } from '../../models/organization';
import { IVarianceReportItem } from '../../models/reports/variance-report-item';
import { UrlService } from '../url.service';
import { UserService } from '../user/user.service';
import { saveAs } from 'file-saver';
import { FileUtilities } from '../../shared/utilities/file-utilities';
import { IAnnualBudgetReportReportItem } from '../../models/reports/annual-budget-report-item';
@Injectable({
  providedIn: 'root',
})
export class ReportService implements OnInit {
  getOrganizationLogoUrl(organizationId: string): Observable<{ logo }> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/logo`;

    return this.http.get<{ logo }>(`${url}`);
  }

  getOrganizationLogo(
    organizationId: string,
    logoUrl: string
  ): Observable<HTMLImageElement> {
    return new Observable((observer) => {
      var img = new Image();
      img.src = FileUtilities.fileUrl(logoUrl);
      img.onload = (e: any) => {
        observer.next(img);
        observer.complete();
      };
    });
  }

  getReportLogo(): HTMLImageElement {
    var img = new Image();

    img.src = '/assets/images/logo.png';
    img.onload = ($event) => {
      console.log('$event', $event);
    };

    return img;
  }
  protected collectionname = 'reports';

  user: IMember;
  organization: IOrganization;

  @Select(UserState.getCurrentOrganization)
  organization$: Observable<IOrganization>;

  @Select(UserState.getCurrentUser)
  member$: Observable<IMember>;

  ngOnInit(): void {
    this.setCollectionName('reports');
  }

  private setCollectionName(collectionName: string): void {
    this.collectionname = collectionName;
  }

  treasurerReport(
    organizationId: string,
    month: number,
    year: number
  ): Observable<any> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/treasurer/${year}/${month}`;

    return this.http.get<ITreasurerReport>(`${url}`);
  }

  treasurerReportSummary(
    organizationId: string,
    month: number,
    year: number
  ): Observable<any> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/treasurer-budget-summary/${year}/${month}`;

    return this.http.get<ITreasurerReport>(`${url}`);
  }

  /**
   * Backfills missing account-balance records for an organization on demand.
   * When year/month are provided, backfills that specific month (carrying
   * forward the calculated ending balance from the prior month) — used by
   * the treasurer report page to fill in whichever month is being viewed.
   * Without year/month, falls back to the daily scheduled job's current +
   * next month logic.
   *
   * Pass recalculate: true to also force-recalculate records that already
   * have a real (non-$0, non-manual) balance, not just $0 placeholders —
   * used by the report page's "Recalculate Balances" action.
   */
  ensureAccountBalances(
    organizationId: string,
    year?: number,
    month?: number,
    recalculate?: boolean
  ): Observable<any> {
    const url = this.urlService.buildUrl('account-balance-rollover', 'ensure');
    const body: any = { organizationId };
    if (year != null && month != null) {
      body.year = year;
      body.month = month;
      if (recalculate) {
        body.recalculate = true;
      }
    }
    return this.http.post<any>(`${url}`, body);
  }

  treasurerReportByCategory(
    organizationId: string,
    month: number,
    year: number
  ): Observable<any> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/treasurer-by-category/${year}/${month}`;

    return this.http.get<ITreasurerReport>(`${url}`);
  }

  getCategories(organizationId: string): Observable<string[]> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/categories`;

    return this.http.get<string[]>(`${url}`);
  }

  getBudgetItems(organizationId: string): Observable<string[]> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/budget-items`;

    return this.http.get<string[]>(`${url}`);
  }

  transactionsByAccount(
    organizationId: string,
    accountId: string,
    startDate?: Date,
    endDate?: Date
  ): Observable<any> {
    let url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/transactionsByAccount/${accountId}`;

    // Add date range query params if provided
    if (startDate && endDate) {
      const params = new URLSearchParams();
      params.set('startDate', startDate.toISOString());
      params.set('endDate', endDate.toISOString());
      url += `?${params.toString()}`;
    }

    return this.http.get(`${url}`);
  }

  transactionsByMember(
    organizationId: string,
    accountId: string
  ): Observable<any> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/transactionsByMember/${accountId}`;

    return this.http.get(`${url}`);
  }

  varianceReport(termId: string): Observable<IVarianceReportItem[]> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}variance/${termId}`;

    return this.http.get<IVarianceReportItem[]>(`${url}`);
  }

  annualBudgetSummary(
    termId: string
  ): Observable<{ budgetItems: IAnnualBudgetReportReportItem[] }> {
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}annual-budget-summary/${termId}`;

    return this.http.get<{ budgetItems: IAnnualBudgetReportReportItem[] }>(
      `${url}`
    );
  }

  transactionsByCategory(
    organizationId: string,
    category: string,
    startDate?: Date,
    endDate?: Date
  ): Observable<any> {
    let url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/transactionByCategory/${category}`;

    // Add date range query params if provided
    if (startDate && endDate) {
      const params = new URLSearchParams();
      params.set('startDate', startDate.toISOString());
      params.set('endDate', endDate.toISOString());
      url += `?${params.toString()}`;
    }

    return this.http.get(`${url}`).pipe(
      map((item) => {
        this.setId(item);
        return item;
      }),
      catchError(this.errorMgmt)
    );
  }

  transactionsByBudgetItem(
    organizationId: string,
    budgetItem: string,
    startDate?: Date,
    endDate?: Date,
    year?: number,
    month?: number
  ): Observable<any> {
    // The name goes in the query string, not the path: names can contain "/",
    // and the production IIS proxy decodes %2F into a path separator.
    const url = `${this.urlService.buildUrl(
      this.collectionname
    )}${organizationId}/budget-item`;

    const params = new URLSearchParams();
    params.set('name', budgetItem);
    // year/month scope the backend to the same pipeline used to compute the
    // budget-summary totals, so buckets like "Unclassified" drill down
    // correctly. Falls back to the legacy startDate/endDate lookup when
    // omitted, for callers not yet passing the report period.
    if (year && month) {
      params.set('year', year.toString());
      params.set('month', month.toString());
    } else if (startDate && endDate) {
      params.set('startDate', startDate.toISOString());
      params.set('endDate', endDate.toISOString());
    }
    return this.http.get(`${url}?${params.toString()}`);
  }
  constructor(
    protected http: HttpClient,
    protected urlService: UrlService,
    protected userService: UserService,
    protected store: Store
  ) {
    this.organization$.subscribe((org) => {
      this.organization = org;
    });

    this.member$.subscribe((member) => {
      this.user = member;
    });

    if (this.user) return;
    // TODO: move this to a userService and out of state.
    try {
      this.userService.getCurrentUser().then(
        (user) => {
          // console.log('in getCurentUser', this.user, this.organization)
          if (!!this.user && !!this.organization) return;
          this.store
            .dispatch(new user.Actions.GetLoginInformation(user.email))
            .subscribe((response) => {});
        }
        // (error) => console.log('no user logged in', error)
      );
    } catch {}
  }

  setId(item): void {
    if (item && item['_id'] && !item.id) {
      item['id'] = item['_id'];
    }
  }

  errorMgmt(error: HttpErrorResponse) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    return throwError(errorMessage);
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    saveAs(data, fileName + EXCEL_EXTENSION);
  }

  scaleImage(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    const aspectRatio: number = originalWidth / originalHeight;

    // Calculate scaling factors for width and height
    const widthScaleFactor: number = maxWidth / originalWidth;
    const heightScaleFactor: number = maxHeight / originalHeight;

    // Choose the smaller scaling factor to maintain aspect ratio and fit within constraints
    const scaleFactor: number = Math.min(widthScaleFactor, heightScaleFactor);

    // Scale the dimensions based on the chosen factor
    const scaledWidth: number = originalWidth * scaleFactor;
    const scaledHeight: number = originalHeight * scaleFactor;

    return { width: scaledWidth, height: scaledHeight };
  }
}
